<!-- 自定义table组件 -->
<script setup lang="tsx">
import type { TableColumnType, TablePaginationConfig } from 'ant-design-vue';
import { get } from 'lodash-es';
import type { Ref } from 'vue';
import { computed, ref, useAttrs, watch } from 'vue';
import { useAppStore } from '@/store/modules/app';
import { request } from '@/service/request';
import { useTableScroll } from '@/hooks/common/useTableScrollHook';

interface MyTableProps {
  api?: string;
  columns?: TableColumnType[];
  // 请求体中
  searchParams?: Record<string, any>;
  // url中
  queryParams?: Record<string, any>;
  rowKey?: string;
  resDataKey?: string | string[];
  countKey?: string | string[];
  pageKey?: string;
  pageSizeKey?: string;
  requestMethod?: 'get' | 'post';
  isUseOffset?: boolean;
  emptyText?: string;
  immediate?: boolean;
  paginationConfig?: boolean | TablePaginationConfig;
  onResponse?: (response: Record<string, any>) => { data: any[]; count: number };
  scrollX?: number | string;
  scrollY?: number | string | false;
  diffScrollY?: number;
}
const props = withDefaults(defineProps<MyTableProps>(), {
  api: '',
  diffScrollY: 72,
  columns: () => [],
  emptyText: '--',
  requestMethod: 'get',
  searchParams: () => ({}),
  pageKey: 'offset',
  pageSizeKey: 'limit',
  queryParams: () => ({}),
  paginationConfig: undefined,
  onResponse: undefined,
  isUseOffset: true,
  resDataKey: () => ['data', 'results'],
  countKey: () => ['data', 'count'],
  immediate: true,
  rowKey: 'id',
  scrollX: 702,
  scrollY: undefined
});

// 初始分页参数
const initialPaginationConfig = {
  [props.pageKey]: props.isUseOffset ? 0 : 1,
  [props.pageSizeKey]: 10
};
// 元数据
const dataSource = ref<any[]>([]);
// 分页参数
const paginationInfo = ref(initialPaginationConfig);
// 加载状态
const loading = ref<boolean>(false);
// 总条数
const total = ref<number>(0);
const appStore = useAppStore();
// 没有被props显式定义的属性都在这
const attrs = useAttrs();
const { scrollConfig, tableWrapperRef } = useTableScroll(
  typeof props.scrollX === 'number' ? props.scrollX : 702,
  props.diffScrollY
);

// 添加空占位符
const resColumns = computed(() => {
  return props.columns?.map(item => {
    if (item?.customRender) {
      return item;
    }
    return {
      ...item,
      customRender: ({ text }: any) => text || props.emptyText
    };
  });
});

// 当前页码
const currentPage = computed(() =>
  props.isUseOffset
    ? convertOffsetToPage(paginationInfo.value[props.pageSizeKey], paginationInfo.value[props.pageKey])?.page
    : paginationInfo.value?.[props.pageKey]
);

watch(
  [paginationInfo, () => props.api, () => props.searchParams, () => props.queryParams],
  () => {
    getDataSource(paginationInfo.value);
  },
  { immediate: props.immediate, deep: true }
);
defineExpose<{ loading: Ref<boolean>; refresh: () => void; dataSource: Ref<any[]> }>({
  loading,
  refresh: handleRefresh,
  dataSource
});

async function getDataSource(params: Record<string, any>) {
  let bodyParams = {
    ...props.searchParams
  };
  let urlParams = {
    ...props.queryParams
  };
  if (props.requestMethod !== 'get') {
    bodyParams = {
      ...bodyParams,
      ...params
    };
  } else {
    urlParams = {
      ...urlParams,
      ...params
    };
  }
  try {
    loading.value = true;
    const res = await request<any>({
      url: props.api,
      method: props.requestMethod,
      data: bodyParams,
      params: urlParams
    });
    if (props.onResponse) {
      const { data, count } = props.onResponse(res);
      dataSource.value = data;
      total.value = count;
    } else {
      const d = get(res, props.resDataKey, []);
      const c = props.countKey ? get(res, props.countKey, 0) : d?.length;
      dataSource.value = d;
      total.value = c;
    }
  } catch (error) {
    console.log(error);
    dataSource.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
}

/**
 * 将 limit-offset 模式转换为 page-pageSize 模式
 *
 * @param limit 每页条数
 * @param offset 偏移量
 * @returns 返回 page 和 pageSize
 */
function convertOffsetToPage(limit: number, offset: number): { page: number; pageSize: number } {
  const pageSize = limit;
  const page = Math.floor(offset / limit) + 1;

  return { page, pageSize };
}

/**
 * 将 page-pageSize 模式转换为 limit-offset 模式
 *
 * @param page 页码
 * @param pageSize 每页条数
 * @returns 返回 limit 和 offset
 */
function convertPageToOffset(page: number, pageSize: number): { limit: number; offset: number } {
  const limit = pageSize;
  const offset = (page - 1) * pageSize;

  return { limit, offset };
}

function onTableChange(pagination: TablePaginationConfig) {
  if (props.isUseOffset) {
    const { limit, offset } = convertPageToOffset(pagination?.current || 1, pagination?.pageSize || 10);
    paginationInfo.value = {
      [props.pageSizeKey]: limit,
      [props.pageKey]: offset
    };
  } else {
    paginationInfo.value = {
      [props.pageKey]: pagination?.current || 1,
      [props.pageSizeKey]: pagination?.pageSize || 10
    };
  }
}

// 刷新表格数据
function handleRefresh() {
  getDataSource(paginationInfo.value);
}

const newScrollConfig = computed(() => {
  const config: Record<string, any> = {
    ...scrollConfig.value,
    x: props.scrollX
  };
  if (props.scrollY === false) {
    Reflect.deleteProperty(config, 'y');
  } else if (props.scrollY !== undefined) {
    config.y = props.scrollY;
  }
  return config;
});
</script>

<template>
  <ATable
    ref="tableWrapperRef"
    :columns="resColumns"
    :data-source="dataSource"
    :row-key="props.rowKey"
    size="small"
    :loading="loading"
    :pagination="
      props.paginationConfig === false
        ? false
        : {
          current: currentPage as number,
          total,
          showSizeChanger:true,
          defaultPageSize:10,
          size:'small',
          simple: appStore.isMobile,
          showTotal: (total, range) => `总共${total}条数据，当前显示${range[0]}-${range[1]}条`,
            ...(props.paginationConfig || {}) as TablePaginationConfig
          }
    "
    class="h-full"
    v-bind="attrs"
    :scroll="newScrollConfig"
    @change="onTableChange"
  >
    <slot />
  </ATable>
</template>

<style lang="scss" scoped></style>
